/**
 * An ambassador's own record, theirs to keep right — 29 Aug 2026.
 *
 * The fields here are the ones somebody may state about themselves: how to
 * reach them, where they ride, their handles, their merch size. Everything the
 * *programme* decides — status, compensation, campaign code, what they have
 * delivered — is absent, and absent from the Firestore rule too, so an
 * ambassador who could get past this form still could not promote themselves.
 *
 * Saved on an explicit press rather than on blur. Blur-saving is right for a
 * note nobody else reads; it is wrong for a form somebody is filling in on a
 * phone, where every field they tab through would be a write.
 */
import { useEffect, useState } from 'react'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { useAuth } from '@/features/auth/AuthProvider'

/** Exactly the fields the rule permits — see `firestore.rules`. */
const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'postalAddress', label: 'Postal address', hint: 'Where merch should be sent.' },
  { key: 'tshirt', label: 'T-shirt size' },
  { key: 'homeArea', label: 'Home area', hint: 'Where you actually ski, in your own words.' },
  { key: 'oneLiner', label: 'One line about you' },
  { key: 'website', label: 'Website' },
  { key: 'instagram', label: 'Instagram', hint: 'Handle only — no @, no link.' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'linkedin', label: 'LinkedIn' },
] as const

type Form = Record<string, string>

export function AmbassadorScreen() {
  const { user } = useAuth()
  const id = user?.ambassadorId ?? ''
  const [form, setForm] = useState<Form | null>(null)
  const [folder, setFolder] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    if (!id) {
      setForm({})
      return
    }
    void getDoc(doc(db, 'skidaCrmPeople', id))
      .then((snap) => {
        if (!live) return
        const d = snap.data() ?? {}
        const next: Form = {}
        for (const f of FIELDS) next[f.key] = String(d[f.key] ?? '')
        setForm(next)
        // Read, never written from here — see the note by the link itself.
        setFolder(String(d.driveFolderUrl ?? '').trim())
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not read your record.')
      })
    return () => {
      live = false
    }
  }, [id])

  const save = async () => {
    if (!form || !id) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      // Only the permitted keys, and `updatedAt` because the rule allows it and
      // somebody should be able to see the record is current.
      await updateDoc(doc(db, 'skidaCrmPeople', id), {
        ...Object.fromEntries(FIELDS.map((f) => [f.key, form[f.key] ?? ''])),
        updatedAt: new Date().toISOString(),
      })
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Nothing was changed.')
    } finally {
      setSaving(false)
    }
  }

  if (!id) {
    return (
      <p className="empty-note">
        Your sign-in is not yet linked to an ambassador record. Ask Skida to connect it.
      </p>
    )
  }
  if (!form) return <p className="empty-note">Loading…</p>

  return (
    <section className="outside__card">
      <h2 className="outside__title">Your details</h2>
      <p className="outside__lede">
        Keep this current and we will use it — merch goes to the address below, and your handles are
        how we credit you. Nothing here is visible to anybody outside Skida.
      </p>

      <div className="outside__form">
        {FIELDS.map((f) => (
          <label className="field" key={f.key}>
            <span>{f.label}</span>
            <input
              className="input"
              value={form[f.key] ?? ''}
              onChange={(e) => {
                setSaved(false)
                setForm({ ...form, [f.key]: e.target.value })
              }}
            />
            {'hint' in f && f.hint && <span className="field__note">{f.hint}</span>}
          </label>
        ))}
      </div>

      {error && <p className="recform__error">{error}</p>}

      <div className="outside__actions">
        <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="outside__saved">Saved.</span>}
      </div>

      {/*
        Where they put photos and footage — 29 Aug 2026.

        A folder in the shared Medialibrary drive, made by hand and shared with
        them. Google Drive does the access control, which is the point: Cloud
        Storage rules cannot read Firestore and so cannot see the allowlist, and
        keying a bucket folder to the signed-in address alone would open it to
        anybody who registers a verified email. Drive already knows who this
        person is.

        The link is shown, not edited. Repointing their own record at another
        folder is not theirs to do, and the Firestore rule does not permit it.
      */}
      <div className="outside__folder">
        <div className="card-label outside__sublabel">Your upload folder</div>
        {folder ? (
          <>
            <p className="outside__lede">
              Photos and footage you are happy for us to use go here. Anything in it we may use in
              our channels and marketing, credited to you.
            </p>
            <a className="btn btn--outline" href={folder} target="_blank" rel="noreferrer">
              Open your folder
            </a>
          </>
        ) : (
          <p className="empty-note">
            Your folder has not been set up yet. Ask Skida for it — everything else on this page
            works in the meantime.
          </p>
        )}
      </div>

    </section>
  )
}
